/**
 * How hard the ranger is to notice (02 §6.2, ROUND7 §1): what a man hears behind him, how much of the
 * ranger he must see, and how fast the meter fills once he does. Perception multiplies these; they live
 * apart so the HUD can ask the same questions the garrison does and never draw a different answer. Pure.
 */

import { rangerLit } from '../hazards/terrain';
import { waterAt } from '../statics';
import { HAZARDS } from '../tuning';
import { STEALTH } from '../tuningStealth';
import type { MissionDef, Ranger, TimeOfDay, World } from '../types';
import { lightning, muffle } from '../weather';

/** How he is moving, as an ear hears it. */
export type Motion = keyof typeof STEALTH.hear;

/** How he holds himself, as an eye sees it (and as the ghillie wrap is rated). */
export type Posture = keyof typeof STEALTH.ghillie;

/**
 * Crouched right now. The flag alone is not enough: a wound or a lorry lays him flat mid-tick, before
 * movement has had its turn to clear it, and a man on the ground is crawling whatever the flag says.
 */
export function isSneaking(r: Ranger): boolean {
  return r.sneaking === true && r.stance === 'stand' && !r.running && r.dragging < 0;
}

export function rangerPosture(r: Ranger): Posture {
  return r.stance === 'crawl' ? 'crawl' : isSneaking(r) ? 'sneak' : 'stand';
}

export function rangerMotion(r: Ranger): Motion {
  if (!r.moving) return 'still';
  if (r.running) return 'run';
  // Hauling a body is never quiet: his footsteps are a walker's however low he is (movement.ts).
  if (r.dragging >= 0) return 'walk';
  if (r.stance === 'crawl') return 'crawl';
  return isSneaking(r) ? 'sneak' : 'walk';
}

/**
 * How close a man who is not looking his way notices him, metres. The same things that move his footsteps
 * move this: Light Step quietens a walk, water splashes, deep snow swallows sound (02 §4).
 */
export function hearRange(w: World, def: MissionDef): number {
  const r = w.ranger;
  const motion = rangerMotion(r);
  const range = STEALTH.hear[motion];
  if (range <= 0) return 0;
  const lightStep = motion === 'walk' && r.dragging < 0 ? w.mods.walkNoise : 1;
  const wading = waterAt(def, r.x, r.z) ? HAZARDS.wadeNoise : 1;
  return range * lightStep * wading * muffle(def.biome);
}

/** How much of him a watcher's line must reach to see him, metres above the ground. */
export function sightHeight(r: Ranger): number {
  if (r.stance === 'crawl') return 0.4;
  const upright = r.inTrench ? 0.5 : 1.5;
  return isSneaking(r) ? Math.min(upright, STEALTH.sneakTargetHeight) : upright;
}

/** The light he is seen by: the hour, unless a searchlight has him or lightning lights the whole map. */
export function lightOn(w: World, def: MissionDef): TimeOfDay {
  if (def.timeOfDay === 'day' || rangerLit(w) || lightning(def, def.seed, w.tick) > 0) return 'day';
  return def.timeOfDay;
}

/**
 * Multiplier on how fast a watcher's meter fills for how the ranger holds himself, what he wears and the
 * light (02 §6.2): posture x ghillie wrap x dark. Motion, distance and the watcher's mood are perception's.
 * Crouched is crouched: sneaking in a trench is not counted twice.
 */
export function concealment(w: World, def: MissionDef): number {
  const r = w.ranger;
  const posture = rangerPosture(r);
  const body = posture === 'crawl' ? STEALTH.proneDetection * w.mods.crawlDetection
    : Math.min(r.inTrench ? STEALTH.trenchDetection : 1, posture === 'sneak' ? STEALTH.sneakDetection : 1);
  const wrap = r.gear.ghillie === true ? STEALTH.ghillie[posture] : 1;
  return body * wrap * STEALTH.light[lightOn(w, def)];
}
